// contexts/ThemeContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import $ from 'jquery';
import ApiURl from '../controllers/Api';

const ThemeContext = createContext();

const DEFAULT_THEME = {
  button_bg: '#2B0000',
  button_text: '#F5ECE7',
  button_hover: '#1A0000',
  user_bg: '#E8D9D5',
  user_maroon: '#2B0000',
  user_maroon_deep: '#1A0000',
  user_gold: '#D4AF37',
  user_gold_soft: '#D4AF87',
  user_text: '#2B0000',
  user_text_soft: '#685E59',
  user_card: '#FFFAF5',
  user_border: '#E5D2C7',
  user_cream: '#F5ECE7',
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    ...DEFAULT_THEME,
    loading: true
  });

  const fetchTheme = async () => {
    try {
      const response = await $.getJSON(`${ApiURl}/getColorSettings.php`);
      if (response?.success && response.colors) {
        const nextTheme = {
          ...DEFAULT_THEME,
          button_bg: response.colors.button_bg || response.colors.primary || DEFAULT_THEME.button_bg,
          button_text: response.colors.button_text || response.colors.button_text_color || DEFAULT_THEME.button_text,
          button_hover: response.colors.button_hover || DEFAULT_THEME.button_hover,
          user_bg: response.colors.user_bg || response.colors.background || DEFAULT_THEME.user_bg,
          user_maroon: response.colors.user_maroon || response.colors.primary || DEFAULT_THEME.user_maroon,
          user_maroon_deep: response.colors.user_maroon_deep || DEFAULT_THEME.user_maroon_deep,
          user_gold: response.colors.user_gold || response.colors.accent || DEFAULT_THEME.user_gold,
          user_gold_soft: response.colors.user_gold_soft || DEFAULT_THEME.user_gold_soft,
          user_text: response.colors.user_text || response.colors.text || DEFAULT_THEME.user_text,
          user_text_soft: response.colors.user_text_soft || DEFAULT_THEME.user_text_soft,
          user_card: response.colors.user_card || DEFAULT_THEME.user_card,
          user_border: response.colors.user_border || DEFAULT_THEME.user_border,
          user_cream: response.colors.user_cream || DEFAULT_THEME.user_cream,
          loading: false
        };
        
        setTheme(nextTheme);
        updateCSSVariables(nextTheme);
      } else {
        setTheme({ ...DEFAULT_THEME, loading: false });
        updateCSSVariables(DEFAULT_THEME);
      }
    } catch (error) {
      console.error("Error fetching theme:", error);
      setTheme({ ...DEFAULT_THEME, loading: false });
      updateCSSVariables(DEFAULT_THEME);
    }
  };

  const updateCSSVariables = (colors) => {
    const root = document.documentElement;
    const next = { ...DEFAULT_THEME, ...colors };

    root.style.setProperty('--button-bg', next.button_bg);
    root.style.setProperty('--button-text', next.button_text);
    root.style.setProperty('--button-hover', next.button_hover);
    root.style.setProperty('--user-bg', next.user_bg);
    root.style.setProperty('--user-maroon', next.user_maroon);
    root.style.setProperty('--user-maroon-deep', next.user_maroon_deep);
    root.style.setProperty('--user-gold', next.user_gold);
    root.style.setProperty('--user-gold-soft', next.user_gold_soft);
    root.style.setProperty('--user-text', next.user_text);
    root.style.setProperty('--user-text-soft', next.user_text_soft);
    root.style.setProperty('--user-card', next.user_card);
    root.style.setProperty('--user-border', next.user_border);
    root.style.setProperty('--user-cream', next.user_cream);
  };

  const updateTheme = async (newColors) => {
    try {
      const response = await $.ajax({
        url: `${ApiURl}/updateColorSettings.php`,
        method: 'POST',
        data: newColors
      });
      
      if (response?.success) {
        const nextTheme = {
          ...theme,
          ...newColors,
          loading: false
        };
        setTheme(nextTheme);
        updateCSSVariables(nextTheme);
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      console.error("Error updating theme:", error);
      return { success: false, error };
    }
  };

  useEffect(() => {
    fetchTheme();
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, refreshTheme: fetchTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
